/**
 * Authentication API service
 * Uses axios with credentials to send cookies on every request
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from '../config';

// Axios instance with credentials enabled for cookie support
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies on every request
});

// Single refresh promise shared across all concurrent 401 requests
let refreshPromise: Promise<void> | null = null;

// Response interceptor for automatic token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // If not 401 or no config, reject immediately
    if (error.response?.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    // Skip interceptor for auth endpoints themselves (prevent infinite loops)
    // Note: /auth/me is intentionally excluded so expired tokens can be refreshed on page load
    const isAuthEndpoint = originalRequest.url?.match(/\/auth\/(login|logout|register|refresh)/);
    if (isAuthEndpoint) {
      return Promise.reject(error);
    }

    // If already retrying this request, reject to prevent loops
    if (originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // All concurrent 401s share one refresh call; promise is cleared only after it settles
    if (!refreshPromise) {
      refreshPromise = api.post('/auth/refresh')
        .then(() => {
          refreshPromise = null;
        })
        .catch((err) => {
          refreshPromise = null;
          // Refresh failed, redirect to login only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          throw err;
        });
    }

    try {
      // Wait for the single shared refresh to complete, then retry
      await refreshPromise;
      return api(originalRequest);
    } catch {
      // Refresh failed, reject the original request
      return Promise.reject(error);
    }
  }
);

// User type
export interface User {
  id: number;
  username: string;
  email: string;
  is_admin: boolean;
}

// Auth response type
export interface AuthResponse {
  user: User;
}

// Login credentials
export interface LoginCredentials {
  email: string;
  password: string;
}

// Register credentials
export interface RegisterCredentials {
  username: string;
  email: string;
  password: string;
}

/**
 * Auth API methods
 */
export const authAPI = {
  /**
   * Register a new user
   */
  register: (data: RegisterCredentials) => api.post<AuthResponse>('/auth/register', data),

  /**
   * Login existing user
   */
  login: (data: LoginCredentials) => api.post<AuthResponse>('/auth/login', data),

  /**
   * Logout current user
   */
  logout: () => api.post('/auth/logout'),

  /**
   * Refresh access token
   */
  refresh: () => api.post('/auth/refresh'),

  /**
   * Get current user profile
   */
  getMe: () => api.get<AuthResponse>('/auth/me'),
};

export default api;
